
import { Component, Prop, Watch } from 'vue-property-decorator';
import AppSidebar from '@/components/global/sidebar/AppSidebar.vue';
import SidebarCard from '@/components/global/sidebar/SidebarCard.vue';
import { UserRightsMixin } from '@/mixins/UserRights';
import { mixins } from 'vue-class-component';
import Client from '@/models/Client';
import ClientViewSidebarGeneralInfo from '@/components/global/sidebar/ClientViewSidebarGeneralInfo.vue';
import ClientViewSidebarTags from '@/components/global/sidebar/ClientViewSidebarTags.vue';
import ClientViewSidebarDefaultCommercialist from '@/components/global/sidebar/ClientViewSidebarCommercialist.vue';
import ClientViewSidebarOfficeUser from '@/components/global/sidebar/ClientViewSidebarOfficeUser.vue';
import ClientViewSidebarAttachments from '@/components/global/sidebar/ClientViewSidebarAttachment.vue';
import ClientViewSidebarHistory from '@/components/global/sidebar/ClientViewSidebarHistory.vue';
import ClientViewSidebarCreationSource from '@/components/global/sidebar/ClientViewSidebarCreationSource.vue';
import { badPayerRegex } from '@/regex/formRegex';
import { isArchitectClientType } from '@/helpers/ClientTypeHelper';

@Component({
    name: 'ClientViewSidebar',
    components: {
        AppSidebar,
        SidebarCard,
        ClientViewSidebarGeneralInfo,
        ClientViewSidebarTags,
        ClientViewSidebarDefaultCommercialist,
        ClientViewSidebarAttachments,
        ClientViewSidebarHistory,
        ClientViewSidebarOfficeUser,
        ClientViewSidebarCreationSource,
    },
})
export default class ClientViewSidebar extends mixins<UserRightsMixin>(UserRightsMixin) {
    @Prop({ required: true }) private client!: Client;
    @Prop({ required: true }) private value!: any;
    @Prop({ default: false }) private isReadOnly!: boolean;

    private loading = false;

    private clientTypes: { typeTags: string[]; restrictionTags: string[] } = { typeTags: [], restrictionTags: [] };

    private get generalInfo() {
        if (this.client) {
            return {
                id: this.client.id,
                ingeniousId: this.client.visibleId,
                bcId: this.client.bcId,
                isRoltekPartner: this.client.roltekAppPartner,
                isRoltek: this.isRoltekUG,
            };
        }
    }

    private get isClientBadPayer(): boolean {
        if (!this.client || !this.isRoltekUG) {
            return false;
        }

        return this.client.clientTypes.some((type) => !!type.code?.match(badPayerRegex));
    }

    private get isClientArchitect(): boolean {
        if (!this.client) {
            return false;
        }

        return this.client.clientTypes.some(isArchitectClientType);
    }

    private get isRoltekUG() {
        return this.$store.getters['jwtData/isRoltek'];
    }

    private get isAnyRolteUG(): boolean {
        return this.$store.getters['jwtData/isAnyRoltekUserGroup'];
    }

    public get isAdmin(): boolean {
        return this.isSuperAdmin;
    }

    private get isAllowedToEditRestrictions(): boolean {
        return this.isRoltekUG && this.canEditClientRestrictions;
    }

    private get canSeeLabelTags(): boolean {
        return this.isAnyRolteUG;
    }

    private get canEditLabelTags(): boolean {
        return this.canEditClientRestrictions;
    }

    private setInitialClientTypeIds() {
        const clientTypes = this.client?.clientTypes;

        if (!clientTypes?.length) {
            return;
        }

        clientTypes.forEach((type) => {
            if (type.type === 'TYPE') {
                this.clientTypes.typeTags.push(type.id);
            } else if (type.type === 'RESTRICTION') {
                this.clientTypes.restrictionTags.push(type.id);
            }
        });
    }

    @Watch('clientTypes', {
        immediate: false,
        deep: true,
    })
    private onClientTypesChange() {
        let clientTags: string[] = [];

        if (this.clientTypes.typeTags.length || this.clientTypes.restrictionTags.length) {
            clientTags = [...this.clientTypes.typeTags, ...this.clientTypes.restrictionTags];
        }

        this.$emit('input', {
            ...this.value,
            tags: clientTags,
        });
    }

    @Watch('client', {
        immediate: true,
    })
    private onClientChange() {
        if (!this.client) {
            this.loading = true;
        }
        if (this.client) {
            this.loading = false;
        }
    }

    private mounted() {
        this.setInitialClientTypeIds();
    }
}
